import { Controller } from "@hotwired/stimulus"
import ProgressBar from "../helpers/progress_bar"

/* global AbortController */

// Paired with the shared `datatable` controller on the same wrapping element: that one owns the
// DataTables lifecycle, this one owns the four date-range/"no event" checkbox filter groups, the
// misc-filters modal and the "Clear" button.
//
// This page renders no select2 filters, so `reload` below skips the filter-scraping half and just
// merges currentParams() with whatever its own handlers pass in.
//
// Each of the four date filters maps to its own named column in AdminShippingReportDatatable
// (pre_transit_at, in_transit_at, delivered_at, and gifts.redeemed_at), not one shared range.
export default class extends Controller {
  static get targets() {
    return [
      "table",
      "miscFilters",
      "miscBadges",
      "internationalFilter",
      "supplierFilter",
      "sendingOrgFilter",
      "fulfillmentMethodFilter",
    ];
  }

  connect() {
    this.filters = [
      { prefix: "pre-transit", param: "pre_transit_at" },
      { prefix: "in-transit",  param: "in_transit_at"  },
      { prefix: "delivered",   param: "delivered_at"   },
      { prefix: "redeemed",    param: "redeemed_at"    },
    ];

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.filters.forEach(({ prefix, param }) => {
      const gteqEl = this.element.querySelector(`#${prefix}-gteq`);
      const lteqEl = this.element.querySelector(`#${prefix}-lteq`);
      const nullEl = this.element.querySelector(`#${prefix}-null`);

      // Date inputs: reload on change, clear the null flag for that column. The same debounced
      // handler is bound to both inputs, same as jQuery's compound-selector `.on()` did.
      const onDateChange = this.debounce(() => {
        nullEl.checked = false;
        this.reload({
          [param + "_gteq"]: gteqEl.value || null,
          [param + "_lteq"]: lteqEl.value || null,
          [param + "_null"]: null,
        });
      }, 300);
      gteqEl.addEventListener("change", onDateChange, { signal });
      lteqEl.addEventListener("change", onDateChange, { signal });

      // Null checkbox: clear date inputs for that column and reload.
      nullEl.addEventListener("change", (event) => {
        if (event.currentTarget.checked) {
          gteqEl.value = "";
          lteqEl.value = "";
        }
        this.reload({
          [param + "_gteq"]: null,
          [param + "_lteq"]: null,
          [param + "_null"]: event.currentTarget.checked ? "1" : null,
        });
      }, { signal });
    });
  }

  disconnect() {
    this.abortController.abort();
  }

  // data-action="click->admin-shipping-report#applyMiscFilters" on the modal's Apply button.
  applyMiscFilters() {
    this.reload({
      international:      this.internationalFilterTarget.value || null,
      supplier:           this.supplierFilterTarget.value || null,
      sending_org:        this.sendingOrgFilterTarget.value || null,
      fulfillment_method: this.fulfillmentMethodFilterTarget.value || null,
    });
    this.updateMiscFilterBadges();
    $(this.miscFiltersTarget).modal("hide");
  }

  // data-action="click->admin-shipping-report#clearFilters" on the "Clear" button.
  clearFilters() {
    this.filters.forEach(({ prefix }) => {
      this.eachIn(`#${prefix}-gteq, #${prefix}-lteq`, (el) => { el.value = ""; });
      this.element.querySelector(`#${prefix}-null`).checked = false;
    });
    this.internationalFilterTarget.value = "";
    this.supplierFilterTarget.value = "";
    this.sendingOrgFilterTarget.value = "";
    this.fulfillmentMethodFilterTarget.value = "";
    this.updateMiscFilterBadges();
    this.reload({
      pre_transit_at_present: null,
      pre_transit_at_gteq: null, pre_transit_at_lteq: null, pre_transit_at_null: null,
      in_transit_at_gteq:  null, in_transit_at_lteq:  null, in_transit_at_null:  null,
      delivered_at_gteq:   null, delivered_at_lteq:   null, delivered_at_null:   null,
      redeemed_at_gteq:    null, redeemed_at_lteq:    null, redeemed_at_null:    null,
      international: null, sending_org: null, supplier: null, fulfillment_method: null,
    });
  }

  // Scoped native replacement for `$(this.element).find(selector)`: an absent match is a no-op
  // forEach, the same as jQuery's empty-collection behavior on `.val()`/`.prop()`/etc.
  eachIn(selector, callback) {
    this.element.querySelectorAll(selector).forEach(callback);
  }

  updateMiscFilterBadges() {
    const miscFilterDefs = [
      { target: "internationalFilterTarget",      label: "International:",  format: (v) => (v === "true" ? "True" : "False") },
      { target: "supplierFilterTarget",            label: "Supplier: "                                                        },
      { target: "sendingOrgFilterTarget",          label: "Sending org: "                                                     },
      { target: "fulfillmentMethodFilterTarget",   label: "Fulfillment: ", format: (v) => v.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) },
    ];

    this.miscBadgesTarget.replaceChildren();
    miscFilterDefs.forEach(({ target, label, format }) => {
      const val = this[target].value;
      if (!val) { return; }
      const display = format ? format(val) : val;

      // Built as DOM nodes with textContent, not an HTML string: `display` carries admin-typed
      // free text (supplier/sending-org filters) and must never reach innerHTML unescaped.
      const badge = document.createElement("span");
      badge.className = "label label-info filter-template";
      badge.dataset.toggle = "modal";
      badge.dataset.target = "#shipping-misc-filters";

      const labelSpan = document.createElement("span");
      labelSpan.textContent = label;
      const valueSpan = document.createElement("span");
      valueSpan.className = "value";
      valueSpan.textContent = display;

      badge.append(labelSpan, " ", valueSpan);
      this.miscBadgesTarget.append(badge);
    });
  }

  // Without the select2 filter scraping - see the class comment above. Scoped to this.tableTarget,
  // already unique per mount.
  reload(extraParams = {}) {
    ProgressBar.start();

    const params = Object.assign(this.currentParams(), extraParams);
    // `data-source` is a raw, un-namespaced attribute convention shared by every datatable-driving
    // controller in this app - deliberately left off the Values API (see CONVENTIONS.md, "Why isn't this data attribute a Value?").
    const replacement = `${this.tableTarget.getAttribute("data-source").split("?")[0]}?${$.param(params)}`;
    this.tableTarget.setAttribute("data-source", replacement);
    $(this.tableTarget).DataTable().ajax.url(replacement).load(() => ProgressBar.stop());
  }

  currentParams() {
    return this.urlParamsAsObject(this.tableTarget.getAttribute("data-source"));
  }

  // Converts a bracket-notation query string into the nested object `$.param()` re-serializes
  // back into the same form. A URLSearchParams rewrite would lose the nesting.
  // None of this page's own params are bracketed today, but `reload` depends on it.
  urlParamsAsObject(dataSource) {
    const query = dataSource.substring(dataSource.indexOf("?") + 1);
    const regex = /([^&=]+)=?([^&]*)/g;
    const decode = (str) => decodeURIComponent(str.replace(/\+/g, " "));

    const params = {};
    let match = regex.exec(query);
    while (match) {
      const k = decode(match[1]);
      const v = decode(match[2]);

      if (k.substring(k.length - 2) === "[]") {
        const key = k.substring(0, k.length - 2);
        (params[key] || (params[key] = [])).push(v);
      } else {
        params[k] = v;
      }

      match = regex.exec(query);
    }

    const assignPath = (obj, keyPath, value) => {
      const lastKeyIndex = keyPath.length - 1;
      let target = obj;
      for (let i = 0; i < lastKeyIndex; i++) {
        const key = keyPath[i];
        if (!(key in target)) { target[key] = {}; }
        target = target[key];
      }
      target[keyPath[lastKeyIndex]] = value;
    };

    Object.keys(params).forEach((prop) => {
      const structure = prop.split("[");
      if (structure.length > 1) {
        const levels = structure.map((part) => part.replace(/[?[\]\\ ]/g, ""));
        assignPath(params, levels, params[prop]);
        delete params[prop];
      }
    });

    return params;
  }

  debounce(callback, ms) {
    let timer = 0;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => callback(...args), ms);
    };
  }
}
